import type mapboxgl from 'mapbox-gl';

/**
 * Window event the map right-click menu (`apps/map/components/MapContextMenu.vue`) listens for.
 * Any map page that mounts that menu opens it by dispatching this event.
 */
export const MAP_CONTEXT_MENU_EVENT = 'map-app-context-menu';

export interface MapContextMenuDetail {
  x: number;
  y: number;
  lngLat: mapboxgl.LngLat;
  features: mapboxgl.MapboxGeoJSONFeature[];
}

type ContextMenuMap = Pick<mapboxgl.Map, 'getContainer' | 'queryRenderedFeatures'>;
type ContextMenuMouseEvent = Pick<mapboxgl.MapMouseEvent, 'point' | 'lngLat'> & {
  preventDefault?: () => void;
  originalEvent?: { preventDefault?: () => void };
};

/**
 * Handle a Mapbox `contextmenu` event by opening the map right-click menu at the clicked point.
 * Does nothing while waypoint adding mode is on, so a right-click there doesn't stack a menu on
 * top of the "Add waypoint" banner. Returns whether the menu was opened.
 */
export function openMapContextMenu(
  map: ContextMenuMap | null | undefined,
  e: ContextMenuMouseEvent,
  waypointAddingModeEnabled: boolean,
): boolean {
  if (waypointAddingModeEnabled) return false;

  if (typeof e.preventDefault === 'function') {
    e.preventDefault();
  }
  e.originalEvent?.preventDefault?.();

  const container = map?.getContainer();
  if (!map || !container) return false;

  // Mapbox gives the point relative to the map container; the menu is position: fixed.
  const rect = container.getBoundingClientRect();
  const detail: MapContextMenuDetail = {
    x: rect.left + e.point.x,
    y: rect.top + e.point.y,
    lngLat: e.lngLat,
    features: map.queryRenderedFeatures(e.point),
  };

  window.dispatchEvent(new CustomEvent(MAP_CONTEXT_MENU_EVENT, { detail }));
  return true;
}
